import { Box, Button, Card, CardActions, CardContent, CardHeader, FormControlLabel, IconButton, Radio, Stack, Tooltip, Typography } from "@mui/material";
import RefreshIcon from '@mui/icons-material/Refresh';
import DoorbellIcon from '@mui/icons-material/Doorbell';
import { LoadingButton } from "@mui/lab";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { bhpButtonSx } from "../../styles/bhpStyles";
import { ScheduleItem } from "./ScheduleItem";
import { useContext, useEffect, useState } from "react";
import { ScheduleSlot } from "./types/scheduleSlot";
import { DoorState } from "./enums/DoorState";
import dayjs, { Dayjs } from "dayjs";
import weekday from 'dayjs/plugin/weekday';
import updateLocale from 'dayjs/plugin/updateLocale';
import { v4 as uuidv4 } from 'uuid';
import { ScheduleSlotData } from "./types/scheduleSlotData";
import { DayCategoryDto, ScheduleDto, ScheduleItemDto, TimeDto } from "./dtos/ScheduleDto";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import VspPortalContext from "../../store/VspPortalContext";
import { useCommonDoorScheduleHttp } from "./hooks/useCommonDoorScheduleHttp";
import SearchIcon from '@mui/icons-material/Search';
import { VspPortalActions } from "../../store/VspPortalActions";

type EditDoorScheduleProps = {

    dayCategoryName: string;
    doorCode: string;
    villageCode: string;

};

export const EditDoorSchedule: React.FC< EditDoorScheduleProps > = ( props: EditDoorScheduleProps ) => {

    const { dayCategoryName, doorCode, villageCode } = props;

    const [ canSubmit, setCanSubmit ] = useState< boolean >( false );
    const [ dateFrom, setDateFrom ] = useState< Dayjs >( dayjs().startOf('day') );
    const [ dateTo, setDateTo ] = useState< Dayjs | null  >( );
    const [ isNoEndDateDisabled, setIsNoEndDateDisabled ] = useState< boolean >( false );
    const [ isSubmitting, setIsSubmitting ] = useState< boolean >( false );
    const [ lastChangeBy, setLastChangeBy ] = useState< string >( 'N/A' );
    const [ scheduleSlots, setScheduleSlots ] = useState<ScheduleSlot[]>([ ]);
    const [ shouldUseEndDate, setShouldUseEndDate ] = useState<boolean>( false );
    
    const { dispatch, state } = useContext( VspPortalContext );

    const { submitSchedule } = useCommonDoorScheduleHttp( );

    useEffect( () => {

        // set Monday as first day of week
        dayjs.extend(updateLocale);
        dayjs.updateLocale('en', {
            weekStart: 1, // Monday = 1, Sunday = 0
        });

        dayjs.extend( weekday );
        const dateFrom = getDateFrom( dayCategoryName );
        setDateFrom( dateFrom );
        reset( );
        initDayCategories( );

    }, [ dayCategoryName ] );

    
    useEffect( () => {

        const dayCategory = state.schedule?.dayCategories.find( d => d.dayCategory.name.toLowerCase() === dayCategoryName.toLowerCase() );
        const initialTimes: TimeDto[] = dayCategory?.times ? dayCategory.times : [];

        const times: TimeDto[] = [];
        for(const scheduleSlot of scheduleSlots){
           
            times.push({
                time: formatTime( scheduleSlot.actualTime ),
                state: [scheduleSlot.doorState],
            });

        }

        

        if( JSON.stringify( initialTimes ).toLowerCase() == JSON.stringify( times ).toLowerCase() ) {

            setCanSubmit(false);
            setIsNoEndDateDisabled( false );

            let originalEndDate: Dayjs | null = dayjs( dayCategory?.dayCategory.endDate, 'DD/MM/YYYY' );
            if(dayCategory?.dayCategory.endDate == null){
                originalEndDate = null;
            }

            if( dateTo == null && originalEndDate != null){
                
                setCanSubmit( true );

            }
            
            if( dateTo != null && originalEndDate!= null && dateTo?.format( 'YYYY-MM-DD' ) != originalEndDate?.format( 'YYYY-MM-DD' )) {
                
                setCanSubmit( true );

            }

        } else {

            if( times.length > 0) {

                setShouldUseEndDate( true ); // exclude setting when initial loading

            }


            setIsNoEndDateDisabled( true );
            
            if( dateTo != null ) {
                
                setCanSubmit( true );

            } else {

                setCanSubmit( false );
            }

        }

    }, [ dateTo, scheduleSlots ]);


    const formatTime = ( date: Dayjs ): string => {

        let hour = date.hour().toString();
        let minute = date.minute().toString();

        if(hour.length === 1){
            hour = '0' + hour;
        }

        if(minute.length === 1){
            minute = '0' + minute;
        }

        return `${hour}:${minute}`;

    };


    const getDateFrom = ( day: string ) => {

        const thisWeekMonday = dayjs().weekday( 0 ).startOf('day');
        let dayIncrement = 0;

        switch(day)
        {
            case 'Monday':
                dayIncrement += 0;
                break;
            case 'Tuesday':
                dayIncrement += 1;
                break;
            case 'Wednesday':
                dayIncrement += 2;
                break;
            case 'Thursday':
                dayIncrement += 3;
                break;
            case 'Friday':
                dayIncrement += 4;
                break;
            case 'Saturday':
                dayIncrement += 5;
                break;
            case 'Sunday':
                dayIncrement += 6;
                break;
        }

        const firstSuchDayOfWeek = thisWeekMonday.add( dayIncrement, 'day' );
        const today = dayjs().startOf('day');

        if(firstSuchDayOfWeek.isAfter(today)){
            return firstSuchDayOfWeek;
        }

        return firstSuchDayOfWeek.add( 7, 'day' );

    };


    const handleCancelClick = ( ) => initDayCategories( );


    const handleDateToChange = ( date: Dayjs | null ) => {

        if( !date ){
            
            return;

        }

        setDateTo( date );
        
    };


    const handleResetClick = ( ) => initDayCategories( );


    const handleAddRow = ( scheduleSlot: ScheduleSlot ) => {

        const updatedScheduleSlots = [ ...scheduleSlots, scheduleSlot ];
        updateSlotsState( updatedScheduleSlots );
        setScheduleSlots( updatedScheduleSlots );

    };


    const handleEditScheduleSlot = ( scheduleSlotData: ScheduleSlotData ) => {

        const updatedScheduleSlots = [ ...scheduleSlots ];
        
        const updatedScheduleSlot = updatedScheduleSlots.find( x => x.slotId === scheduleSlotData.slotId );

        
        if( updatedScheduleSlot ) {

            const isLastRow = scheduleSlots[ scheduleSlots.length - 1].slotId == updatedScheduleSlot.slotId;
            
            updatedScheduleSlot.actualTime = scheduleSlotData.actualTime;
            updatedScheduleSlot.doorState = scheduleSlotData.doorState;

            const fiveMinutesBeforeMidnight = updatedScheduleSlot.actualTime.startOf('day').add( 23, 'hour' ).add( 50, 'minute' );
                
            if(isLastRow){
            
                if( updatedScheduleSlot.actualTime.isBefore( fiveMinutesBeforeMidnight ) ){ // cannot add row after 23:50

                    updatedScheduleSlot.canAddRow = true;

                } else {

                    updatedScheduleSlot.canAddRow = false;

                }
            
            }

            setScheduleSlots( updatedScheduleSlots );
            
        }

    };


    const handleOkClick = async () => {

        const times: TimeDto[] = [];

        for(const scheduleSlot of scheduleSlots){
           

            if( scheduleSlot.doorState == DoorState.SecureUsePin ){

                times.push({
                    time: formatTime( scheduleSlot.actualTime ),
                    state: [ DoorState.Secure, DoorState.UsePin ],
                });


            } else if ( scheduleSlot.doorState == DoorState.FreeUsePin ) {

                times.push({
                    time: formatTime( scheduleSlot.actualTime ),
                    state: [ DoorState.Free, DoorState.UsePin ],
                });
            
            } else {

                times.push({
                    time: formatTime( scheduleSlot.actualTime ),
                    state: [scheduleSlot.doorState],
                });

            }

        }

        const dayCategoryDto: DayCategoryDto = { 

            name: dayCategoryName,
            startDate: dateFrom.format( 'YYYY-MM-DD' ),
            endDate: dateTo ? dateTo.format( 'YYYY-MM-DD' ) : '',
            lastUpdatedDateTime: dayjs().format( 'YYYY-MM-DD HH:mm:ss' ),
            lastUpdatedBy: state.username,
            override: true,

        };

        const scheduleDto: ScheduleDto = {
            
            code: state.schedule!.code,
            dayCategories: [
                {
                    dayCategory: dayCategoryDto,
                    times: times,
                }
            ]

        };
        

        setIsSubmitting( true );
        const isSuccess = await submitSchedule( state.schedule!.code, villageCode, scheduleDto );
        setIsSubmitting( false );

        if( isSuccess ){

            setCanSubmit( false );
            setLastChangeBy( state.username );

            const schedule = { ...state.schedule };

            if( !schedule.dayCategories ){

                return; 

            }

            const dayCategoryIndex = schedule.dayCategories.findIndex( m => m.dayCategory.name === dayCategoryName);
            
            schedule.dayCategories[dayCategoryIndex] = {
    
                dayCategory: dayCategoryDto,
                times: times,
    
            };
            dispatch({ type: VspPortalActions.SetSchedule, payload: schedule });

        }


    };


    const handleRemoveRow = ( slotId: string) => {

        const slotIndex = scheduleSlots.findIndex(x => x.slotId === slotId);
        const updatedScheduleSlots = [ ...scheduleSlots ];
        updatedScheduleSlots.splice( slotIndex, 1 );

        updateSlotsState( updatedScheduleSlots );

        setScheduleSlots( updatedScheduleSlots );

    };


    const handleShouldUseEndDateChange = () => {
        
        setDateTo( null );
        
        setShouldUseEndDate(prev => { 
        
            return !prev; 

        })
        
    };


    

    const initDayCategories = () => {

        setCanSubmit( false );
        setIsNoEndDateDisabled( false );

        const dayCategory = state.schedule?.dayCategories.find( d => d.dayCategory.name.toLowerCase() === dayCategoryName.toLowerCase() );

        if(dayCategory?.dayCategory.lastUpdatedBy) {

            setLastChangeBy( dayCategory?.dayCategory.lastUpdatedBy );

        }

        if( dayCategory?.dayCategory.endDate ) {

            setShouldUseEndDate( true );
            let endDate = dayjs( dayCategory?.dayCategory.endDate, 'DD/MM/YYYY' );

            if( !endDate.isValid( ) ) { // TODO: use the same date format everywhere. Discuss it with Min
                
                endDate = dayjs( dayCategory?.dayCategory.endDate, 'YYYY-MM-DD' );

            }

            setDateTo( endDate );

        } else {

            setShouldUseEndDate( false );
            setDateTo( null );

        }

        const times: TimeDto[] | undefined = dayCategory?.times;

        if( times ){
            
            const scheduleSlots: ScheduleSlot[] = [ ];
            
            for(const time of times){

                scheduleSlots.push({
                    actualTime: mapTime( time.time ),
                    canAddRow: false,
                    canEditTime: false,
                    canRemoveRow: false,
                    doorState: mapDoorState( time.state ),
                    minTime: mapTime( time.time ),
                    slotId: uuidv4(),
                });

            }

            setScheduleSlots( mapScheduleSlots( scheduleSlots ) );

        }

    };


    const mapDoorState = ( doorStates: string[] ): DoorState => {

        const lowerCaseDoorStates = doorStates.map( doorState => doorState.toLowerCase() );
        
        
        if(lowerCaseDoorStates.includes(DoorState.UsePin.toLowerCase())){

            
            if(lowerCaseDoorStates.includes(DoorState.Free.toLowerCase())){

                return DoorState.FreeUsePin;

            }

            return DoorState.SecureUsePin;
            
        }

        if( doorStates[0]?.toLowerCase() == DoorState.Free.toLowerCase() ){

            return DoorState.Free;

        }

        return DoorState.Secure;

    };


    const mapScheduleSlots = ( scheduleSlots: ScheduleSlot[] ): ScheduleSlot[] => {

        const mappedScheduleSlots: ScheduleSlot[] = [];
   

        for( let i = 0; i < scheduleSlots.length; ++i ){

            let mappedSlot = { ... scheduleSlots[ i ] };

            if( i != 0){

                mappedSlot.canRemoveRow = true; // cannot remove very first row
                mappedSlot.minTime = scheduleSlots[ i - 1 ].actualTime.add( 5, 'minute' ); // min time is 5 minutes more than the previous slot time

            }
            
            if( i === scheduleSlots.length - 1 ) {
                
                mappedSlot.canEditTime = true; // can adit time for the last row
                const fiveMinutesBeforeMidnight = mappedSlot.actualTime.startOf('day').add( 23, 'hour' ).add( 50, 'minute' );
                
                if( mappedSlot.actualTime.isBefore( fiveMinutesBeforeMidnight ) ){ // can edit row before 23:50

                    mappedSlot.canAddRow = true;

                }

            }


            mappedScheduleSlots.push( mappedSlot );

        }


        return mappedScheduleSlots;


    };


    const mapTime = ( time: string ): Dayjs => {

        const splittedTime = time.split(':');
        return dayjs( ).set('hour', +splittedTime[0] ).set('minute', +splittedTime[1] );

    };

    
    const updateSlotsState = ( updatedScheduleSlots: ScheduleSlot[] ) => {
        
        for( let updatedScheduleSlot of updatedScheduleSlots ){
            updatedScheduleSlot.canAddRow = false;
            updatedScheduleSlot.canEditTime = false;
            updatedScheduleSlot.canRemoveRow = true;
        }

        updatedScheduleSlots[0].canRemoveRow = false;
        
        if( updatedScheduleSlots.length > 1 ){
            
            const fiveMinutesBeforeMidnight = updatedScheduleSlots[updatedScheduleSlots.length - 1].actualTime.startOf('day').add( 23, 'hour' ).add( 50, 'minute' );

            if( updatedScheduleSlots[updatedScheduleSlots.length - 1].actualTime.isBefore( fiveMinutesBeforeMidnight ) ){ // cannot add row after 23:50
            
                updatedScheduleSlots[updatedScheduleSlots.length - 1].canAddRow = true;                

            }

            updatedScheduleSlots[updatedScheduleSlots.length - 1].canEditTime = true;
        }


    };


    const reset = () => {

        setLastChangeBy( 'N/A' );
        setIsNoEndDateDisabled( false );
        setCanSubmit( false );

    };


    return(
        <>
            <Stack direction="row" display="flex" justifyContent="center" gap={ 0.5 } sx={{ marginTop: '50px', paddingBottom: '15px' }}>

                <Card sx={{ maxWidth: 'fit-content', boxShadow: 5 }}>

                    <CardHeader 
                        sx={{ fontWeight: '600', fontSize: '20px', paddingTop: '5px', paddingBottom: '0' }}
                        title="Edit Door Schedule"
                        avatar={ <DoorbellIcon /> }
                        action={
                            <Tooltip title="Reset">
                                <IconButton onClick={ handleResetClick }>
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        }
                    />

                    <CardContent>

                        <Stack direction="row" display="flex" justifyContent="end" sx={{ marginBottom: '15px' }}>
                            <Typography sx={{ fontWeight: 'bold', fontSize: '0.7rem', lineHeight: 1 }}>Last Change By: { lastChangeBy }</Typography>
                        </Stack>
                        
                        <Stack direction="row" display="flex" justifyContent="center" gap={ 0.5 } alignItems="center" sx={{ borderBottom: '1px solid #C4C4C4', marginBottom: '15px' }}>
                            <Typography>
                                Time
                            </Typography>
                            <Box sx={{ flexGrow: 0.45 }} />
                            <Typography>
                                State
                            </Typography>
                        </Stack>

                        { 

                            scheduleSlots.map(scheduleSlot => 
                                <ScheduleItem 
                                    key={ scheduleSlot.slotId } 
                                    addRow={ handleAddRow } 
                                    editScheduleSlot={ handleEditScheduleSlot }
                                    removeRow={ handleRemoveRow } 
                                    scheduleSlot={ scheduleSlot }
                                />
                            ) 
                            
                        }

                        <table style={{marginTop: '30px'}}>
                            <tbody>
                                <tr>
                                    <td>
                                        <FormControlLabel 
                                            value="!shouldUseEndDate"
                                            control={ <Radio checked={ !shouldUseEndDate } onChange={() => handleShouldUseEndDateChange()} disabled={ isNoEndDateDisabled }/> } 
                                            label="No End Date" 
                                        />
                                    </td>
                                    <td></td>
                                </tr>
                                <tr>
                                    <td>
                                        <FormControlLabel 
                                            value="shouldUseEndDate"
                                            control={ <Radio checked={ shouldUseEndDate } onChange={() => handleShouldUseEndDateChange()}/> } 
                                            label="End Date"
                                        /> 
                                    </td>
                                    <td>
                                        <LocalizationProvider dateAdapter={ AdapterDayjs } adapterLocale="en-au">

                                            <DatePicker 
                                                sx={{ visibility: shouldUseEndDate ? 'visible' : 'hidden'}}
                                                value = { dateTo ? dayjs( dateTo ) : null }
                                                onChange={ handleDateToChange }
                                                minDate = { dateFrom }
                                            />

                                        </LocalizationProvider>
                                    </td>
                                </tr>
                            </tbody>
                        </table>

                    </CardContent>

                    <CardActions
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end"
                        }}
                    >

                        <LoadingButton
                            sx={{ ...bhpButtonSx, width: '100px' }}
                            loadingPosition="start"
                            startIcon={<SearchIcon sx={{ color: 'transparent', display: 'none' }}/>}
                            variant="contained"
                            disabled={ !canSubmit }
                            onClick={ handleOkClick }
                            loading={ isSubmitting }
                        >
                            Ok
                        </LoadingButton>
                        <Button 
                            autoFocus 
                            onClick={ handleCancelClick }
                            sx={{ ...bhpButtonSx, width: '100px' }}
                            // disabled={ isUpdating }
                        >
                            Cancel
                        </Button>
                        
                    </CardActions>

                </Card>
                
            </Stack>
        </>
    );

};
